import { AppThunkAction } from '.';
import { Dictionary, EntityType, IPatch, UpdateOptions } from '../entities/common';
import { post, remove } from './../fetch-interceptor';
import { defaultCatch } from './utils';

export interface BaseSubentitiesUpdateAction<T> {
    entityId: string;
    addOrUpdate?: T[] | T;
    remove?: string[];
    set?: T[];
}

export type ICreationData<IAttrs = Dictionary<any>> = {
    id?: string,
    attributes: IAttrs,
    externalData: Dictionary<any>,
    insertBeforeId?: string,
    parentId?: string
};

export type Actions = {
    create: (entityId: string, subentity: ICreationData) => void;
    update: (enitityId: string, subentityId: string, changes: Dictionary<unknown>, options?: UpdateOptions) => void;
    remove: (entityId: string, ids: string[]) => void;
    bulkCreate: (entityId: string, items: ICreationData[]) => void;
    bulkUpdate: (entityId: string, patches: IPatch<any>[], options?: UpdateOptions) => void;
}

export type StoreActions<T extends string> = {
    [K in keyof Actions as `${K}${T}`]: Actions[K]
}

export const renameActionsFor = <TSuffix extends string>(key: TSuffix, obj: Actions): StoreActions<TSuffix> =>
    Object.keys(obj).reduce((a, b) => ({ ...a, [`${b}${key}`]: obj[b] }), {} as any);

export const actionsForBuilder = <KnownAction>(entityType: EntityType) => <T extends {}>() => ({
    create: <Suffix extends string>(subentityType: Suffix, action: (payload: BaseSubentitiesUpdateAction<T>) => KnownAction) => renameActionsFor(subentityType, {
        update: (entityId, subentityId, changes, options): AppThunkAction<KnownAction> => (dispatch, getState) => {
            return post<T>(`${buildUrl(entityType, entityId, subentityType)}/${subentityId}`, changes)
                .then(data => dispatch(action({ entityId, addOrUpdate: data })))
                .catch(err => { defaultCatch(dispatch)(err); throw err; });
        },
        remove: (entityId, ids): AppThunkAction<KnownAction> => (dispatch, getState) => {
            remove(buildUrl(entityType, entityId, subentityType), { ids })
                .then(_ => dispatch(action({ entityId, remove: ids })))
                .catch(defaultCatch(dispatch));
        },
        create: (entityId, entity): AppThunkAction<KnownAction> => (dispatch, getState) => {
            post<T>(buildUrl(entityType, entityId, subentityType), entity)
                .then(data => dispatch(action({ entityId, addOrUpdate: data })))
                .catch(defaultCatch(dispatch));
        },
        bulkCreate: (entityId, items): AppThunkAction<KnownAction> => (dispatch, getState) => {
            post<T>(`${buildUrl(entityType, entityId, subentityType)}/bulk/create`, items)
                .then(data => dispatch(action({ entityId, addOrUpdate: data })))
                .catch(defaultCatch(dispatch));
        },
        bulkUpdate: (entityId, patches, options): AppThunkAction<KnownAction> => (dispatch, getState) => {
            return post<T>(`${buildUrl(entityType, entityId, subentityType)}/bulk/update`, patches)
                .then(data => dispatch(action({ entityId, addOrUpdate: data })))
                .catch(err => { defaultCatch(dispatch)(err); throw err; });
        }
    })
});

export const buildUrl = (entityType: EntityType, entityId: string, subentityType: string) => `/api/${entityType}/${entityId}/${subentityType}`;